import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Global error handler to catch all application errors, log them to console,
 * and send events to Quantum Metric (QM) with ID 10064.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    // Extract detailed error description
    const errorDetail = this.getDetailedErrorMessage(error);
    console.error(`Global Error Handler: ${errorDetail}`);

    // Send event to Quantum Metric using ID 10064
    this.sendToQuantumMetric(10064, errorDetail);
  }

  /**
   * Extracts a detailed error message from various error types to avoid [object Object]
   */
  private getDetailedErrorMessage(error: any): string {
    let message = '';

    if (error instanceof HttpErrorResponse) {
      message = `API Error - Status: ${error.status}, StatusText: ${error.statusText}, URL: ${error.url}`;
      if (error.error) {
        if (typeof error.error === 'string') {
          message += `, Error Body: ${error.error}`;
        } else if (typeof error.error === 'object') {
          try {
            message += `, Error Body: ${JSON.stringify(error.error)}`;
          } catch {
            message += `, Error Body: [Complex Object]`;
          }
        }
      }
    } else if (error instanceof Error) {
      message = `App Error - Name: ${error.name}, Message: ${error.message}, Stack: ${error.stack}`;
    } else if (typeof error === 'string') {
      message = `String Error - ${error}`;
    } else if (typeof error === 'object' && error !== null) {
      try {
        message = `Object Error - ${JSON.stringify(error)}`;
      } catch {
        message = `Object Error - [Complex Object]`;
      }
    } else {
      message = `Unknown Error Type - ${String(error)}`;
    }

    return message;
  }

  /**
   * Sends event to Quantum Metric (QM)
   */
  private sendToQuantumMetric(id: number, detail: string): void {
    const qmApi = (window as any).QuantumMetricAPI;
    if (qmApi && typeof qmApi.sendEvent === 'function') {
      try {
        // Send the detail string to QM event ID 10064
        // QM sendEvent(id, value, detail) - value 0 for general error
        qmApi.sendEvent(id, 0, detail);
      } catch {
        console.warn('Failed to send event to Quantum Metric');
      }
    } else {
      console.debug('Quantum Metric API not available. Event 10064 not sent.');
    }
  }
}
